// ============================================================================
// Assign a Loginext trip's driver + vehicle across every booking on it. Loginext
// doesn't push these back to us — they're allocated by hand per trip — so ops
// enter them once here and the server fans them out to all the trip's bookings.
// ============================================================================
import { Alert, Form, Input, Modal, Select } from "antd";
import React from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import { assignTripVehicle } from "../logistics.api";

// The fleet partners a trip can be allotted to, in the order ops reads them. A plain constant (not a
// DB table or an enum) — the server stores whatever is sent as free text, so adding or renaming a
// vendor is this one edit and needs no migration. "Other Adhocs" is the catch-all for a one-off
// transporter that isn't on the panel.
const TRANSPORT_VENDORS = ["JMJ", "Pidge", "KGN", "Zippee", "Other Adhocs"];

// Driver numbers are Indian mobiles only, stored in E.164 (+91XXXXXXXXXX). The field shows a fixed
// "+91" and takes just the 10 digits, starting 6-9. Ops were typing the driver's name into the number
// field, so this is what stops a name (or a short/garbled number) from being fanned out to every
// booking on the trip.
const DRIVER_NUMBER_RE = /^[6-9]\d{9}$/;

// Strips everything but digits and caps at 10. A pasted "+91 98765 43210" / "098765 43210" arrives as
// 12 / 11 digits with the country / trunk prefix, so drop that first instead of keeping the wrong end.
// (No `maxLength` on the Input: the browser would truncate the paste before this ever sees it.)
const normalizeDriverNumber = (v?: string) => {
  let d = (v || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d.slice(0, 10);
};

interface Props {
  open: boolean;
  onClose: () => void;
  onAssigned: () => void; // reload the list
}

const AssignTripModal: React.FC<Props> = ({ open, onClose, onAssigned }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    // validateFields REJECTS on an invalid form. Letting that escape `onOk` surfaces as an unhandled
    // rejection ("Uncaught runtime errors: [object Object]" in the dev overlay) on every empty submit
    // — the fields have already been marked in red, so there's nothing left to do but stop here.
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    let res;
    try {
      res = await assignTripVehicle({
        trip_name: values.trip_name.trim(),
        vehicle_number: values.vehicle_number.trim(),
        driver_name: values.driver_name.trim(),
        driver_number: `+91${values.driver_number}`,
        transport_vendor_name: values.transport_vendor_name,
      });
    } finally {
      // Not just on the happy path: a throw here would otherwise wedge the button on "loading".
      setSaving(false);
    }
    // updated_count === 0 means the trip ID matched nothing (usually a typo). The api layer warns;
    // keep the modal open with the values intact so the ID can be corrected rather than re-typed.
    if (res && res.updated_count > 0) {
      form.resetFields();
      onAssigned();
      onClose();
    }
  };

  return (
    <Modal
      title="Assign driver & vehicle to a trip"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Assign"
      confirmLoading={saving}
      destroyOnClose
      centered
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Finds every order on this Loginext trip and puts this driver and vehicle on all of them at once."
        />
        {/* requiredMark left on (the AntD default): every field here is mandatory, so the red * is
            what tells ops that up front rather than after they hit Assign. */}
        <Form form={form} layout="vertical">
          <Form.Item
            label="Trip ID / name"
            name="trip_name"
            // `whitespace` so a space-only entry is caught here rather than trimmed to "" on send.
            rules={[{ required: true, whitespace: true, message: "Trip ID is required" }]}
          >
            <Input placeholder="e.g. TRIP-0001" allowClear />
          </Form.Item>
          <Form.Item
            label="Transport vendor"
            name="transport_vendor_name"
            rules={[{ required: true, message: "Transport vendor is required" }]}
          >
            <Select
              placeholder="Who is running this trip"
              allowClear
              showSearch
              optionFilterProp="label"
              options={TRANSPORT_VENDORS.map((v) => ({ label: v, value: v }))}
            />
          </Form.Item>
          <Form.Item
            label="Vehicle number"
            name="vehicle_number"
            rules={[{ required: true, whitespace: true, message: "Vehicle number is required" }]}
          >
            <Input placeholder="e.g. KA01AB1234" maxLength={32} allowClear />
          </Form.Item>
          <Form.Item
            label="Driver name"
            name="driver_name"
            rules={[
              { required: true, whitespace: true, message: "Driver name is required" },
              // The mirror of the number check: a name with no letters is almost certainly the phone
              // number typed into the wrong box.
              { pattern: /[A-Za-z]/, message: "Enter the driver's name, not a number" },
            ]}
          >
            <Input maxLength={128} allowClear />
          </Form.Item>
          <Form.Item
            label="Driver number"
            name="driver_number"
            // Digits only, capped at 10 — letters and symbols never make it into the field.
            normalize={normalizeDriverNumber}
            rules={[
              { required: true, message: "Driver number is required" },
              { pattern: DRIVER_NUMBER_RE, message: "Enter a valid 10-digit mobile number" },
            ]}
          >
            <Input addonBefore="+91" placeholder="9876543210" inputMode="numeric" allowClear />
          </Form.Item>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default AssignTripModal;
